/**
 * What a saved draft's problems, or a refused post or reverse, ask the person
 * to do: reload, open a lock exception and try again, pick another date, enter
 * an exchange rate, ask Link support, or fix the lines. Pure, so each code's
 * action is testable; the screens only put it into words.
 */
import { isApiError } from "@linkhr/api-client/core";
import { intlLocale } from "@linkhr/i18n";
import {
  ACCOUNT_CHANGE_BLOCKED_REASONS,
  DUAL_CONTROL_ROLES,
  INVENTORY_COUNT_REASONS,
  MOVEMENT_STATES,
  REQUIRED_DIMENSION_REASONS,
  ROLE_ACCOUNT_INVALID_REASONS,
  ROLE_LOCKED_REASONS,
  ROLE_MAPPING_INVALID_REASONS,
  SUPPLIER_PAYMENT_STATES,
} from "../api/codes";
import type { RoleMapping } from "../api/ledger";
import { codesIn, problemArgs, readStale, text } from "../api/problem";
import { codeLabel } from "../components/codeLabel";
import { isolate, type LedgerFormat } from "../ledger/format";

/** A draft problem or a posting violation: `{code, lineNo, args}`, lineNo null for the whole entry. */
export interface Problem {
  lineNo: number | null;
  code: string;
  args: Record<string, unknown>;
}

/**
 * Every code the journal and entry screens can show (the ledger contract's table),
 * and every engine refusal a bill check names in `args.errorCode`, seeded EN and AR.
 */
export const PROBLEM_CODES = [
  "ACCT_ACCOUNT_CURRENCY_MISMATCH",
  "ACCT_ACCOUNT_INACTIVE",
  "ACCT_ACCOUNT_IS_GROUP",
  "ACCT_ACCOUNT_REQUIRED",
  "ACCT_ACCOUNT_RESTRICTED",
  "ACCT_ACCOUNT_SOURCE_NOT_ALLOWED",
  "ACCT_AMOUNT_SCALE_EXCEEDED",
  "ACCT_AUDITOR_PERIOD",
  "ACCT_BEFORE_BOOKS_START",
  "ACCT_CONTROL_ACCOUNT_NEEDS_CONTACT",
  "ACCT_DIMENSION_INACTIVE",
  "ACCT_DIMENSION_REQUIRED",
  "ACCT_DRAFT_STALE",
  "ACCT_ENTRY_ALREADY_REVERSED",
  "ACCT_ENTRY_NOT_REVERSIBLE",
  "ACCT_FX_RATE_MISSING",
  "ACCT_INVARIANT_VIOLATION",
  "ACCT_JOURNAL_OUT_OF_BALANCE",
  "ACCT_JOURNAL_TOO_FEW_LINES",
  "ACCT_LINE_AMOUNT_INVALID",
  "ACCT_LOCK_EXCEPTION_TOO_LONG",
  "ACCT_OPENING_BALANCE_DATE",
  "ACCT_PERIOD_CLOSE_OUT_OF_ORDER",
  "ACCT_PERIOD_HARD_CLOSED",
  "ACCT_PERIOD_NOT_FOUND",
  "ACCT_PERIOD_SOFT_CLOSED",
  "ACCT_PERIOD_STATE_CONFLICT",
  "ACCT_POSTING_SOURCE_CONFLICT",
  "ACCT_ROLE_NOT_MAPPED",
  "ACCT_TAX_DIFFERS_FROM_RATE",
  "ACCT_TAX_RATE_NOT_EFFECTIVE",
  "ENGINE_DEFECT",
] as const;

/** Why an entry can't be reversed: the entry's `notReversibleReason` and the refusal's `reason`. */
export const NOT_REVERSIBLE_REASONS = [
  "SYSTEM",
  "IS_REVERSAL",
  "ALREADY_REVERSED",
  "ALLOCATED",
  "DATE_BEFORE_ORIGINAL",
  "PENDING_TAX_RESOLVED",
] as const;

/** The operations a lock exception lets through (a period refusal's `operation`). */
export const EXCEPTION_OPERATIONS = [
  "POST_MANUAL_JOURNAL",
  "POST_DOCUMENT",
  "BANK_ADJUSTMENT",
  "REVERSE",
  "ALLOCATE",
] as const;
export type Operation = (typeof EXCEPTION_OPERATIONS)[number];
export const isOperation = (value: string): value is Operation =>
  (EXCEPTION_OPERATIONS as readonly string[]).includes(value);

/** Problems as the server lists them, whatever arrives. */
export function readProblems(raw: unknown): Problem[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item: unknown) => {
    if (item === null || typeof item !== "object") return [];
    const { lineNo, code, args } = item as Record<string, unknown>;
    if (typeof code !== "string") return [];
    return [
      {
        lineNo: typeof lineNo === "number" ? lineNo : null,
        code,
        args: args !== null && typeof args === "object" ? (args as Record<string, unknown>) : {},
      },
    ];
  });
}

/** The label key for a problem; a refused reversal says why, a refused source what the account takes. */
export function problemKey(problem: Problem): string {
  if (problem.code === "ACCT_ENTRY_NOT_REVERSIBLE" && text(problem.args.reason) !== null)
    return `accounting:ledger.entry.not_reversible.${String(problem.args.reason)}`;
  if (
    problem.code === "ACCT_ACCOUNT_SOURCE_NOT_ALLOWED" &&
    codesIn(problem.args.allowedSources).length > 0
  )
    return "accounting:ledger.allowed_sources";
  return `accounting:ledger.problems.${problem.code}`;
}

type Translate = (key: string, params: Record<string, unknown>) => string;
type Exists = { exists: (key: string) => boolean };

/** Codes as their labels under `root`, as one list ("supplier bills or supplier credits"). */
export const labelList = (
  f: LedgerFormat,
  t: Translate,
  i18n: Exists,
  root: string,
  codes: readonly string[],
  type: "conjunction" | "disjunction",
): string =>
  new Intl.ListFormat(intlLocale(f.locale), { type }).format(
    codes.map((code) => codeLabel(i18n, t, `${root}.${code}`, code)),
  );

/** A problem as a sentence. Every placeholder any problem uses is always passed. */
export function problemText(
  f: LedgerFormat,
  t: Translate,
  i18n: Exists,
  problem: Problem,
  currency: string,
): string {
  const arg = (name: string): string => text(problem.args[name]) ?? "";
  const day = (name: string): string => (arg(name) !== "" ? f.day(arg(name)) : "");
  return codeLabel(i18n, t, problemKey(problem), problem.code, {
    currency: arg("currency"),
    dimension: arg("dimension"),
    date: day("date"),
    booksStartOn: day("booksStartOn"),
    activeTo: day("activeTo"),
    difference: arg("difference") !== "" ? f.money(arg("difference"), currency) : "",
    sources: labelList(
      f,
      t,
      i18n,
      "accounting:ledger.entry.source",
      codesIn(problem.args.allowedSources),
      "disjunction",
    ),
  });
}

export type Action =
  /** Someone saved the draft first, or approved it, or its figures moved (`reason`): reload it. */
  | { kind: "stale"; name: string | null; reason: string | null }
  /**
   * A soft-closed month: open an exception for each of `operations` (when allowed), then try
   * again. `operation` is the one the refusal leads with; `operations` the ones still missing.
   */
  | {
      kind: "exception";
      periodId: string;
      operation: string;
      operations: string[];
      canOpen: boolean;
      tryAgain: boolean;
    }
  /** A closed month, a date no month covers or a date before the books start: use `date` instead. */
  | { kind: "use_date"; reason: "HARD_CLOSED" | "NOT_FOUND" | "BEFORE_START"; date: string | null }
  /** No exchange rate for the day: enter one. */
  | { kind: "fx"; currency: string; date: string }
  /**
   * No account is set for these default accounts (`roles`, every one named; `qualified` when
   * one is per bank): `roleFix` says who can set them.
   */
  | { kind: "support"; roles: string[]; qualified: boolean }
  /** Each problem says what to fix where it stands. */
  | { kind: "problems" }
  | { kind: "message"; key: string };

/** A refusal's `operations[]`: each operation the date needs, and whether the viewer holds its exception. */
export function readOperations(raw: unknown): { operation: string; held: boolean }[] | null {
  if (!Array.isArray(raw)) return null;
  return raw.flatMap((item: unknown) => {
    if (item === null || typeof item !== "object") return [];
    const { operation, viewerHasException } = item as Record<string, unknown>;
    return typeof operation === "string" ? [{ operation, held: viewerHasException === true }] : [];
  });
}

/** The one thing to do about these problems, or null when each says what to fix where it stands. */
export function actionFor(problems: readonly Problem[]): Action | null {
  const find = (code: string): Problem | undefined => problems.find((p) => p.code === code);
  // Every missing one: naming the first alone meets a second refusal once it is set.
  const unmapped = problems.filter((p) => p.code === "ACCT_ROLE_NOT_MAPPED");
  if (unmapped.length > 0) {
    return {
      kind: "support",
      roles: [...new Set(codesIn(unmapped.map((p) => p.args.role)))],
      qualified: unmapped.some((p) => text(p.args.qualifierId) !== null),
    };
  }
  const fx = find("ACCT_FX_RATE_MISSING");
  if (fx !== undefined) {
    return { kind: "fx", currency: text(fx.args.currency) ?? "", date: text(fx.args.date) ?? "" };
  }
  const early = find("ACCT_BEFORE_BOOKS_START");
  if (early !== undefined) {
    return { kind: "use_date", reason: "BEFORE_START", date: text(early.args.booksStartOn) };
  }
  const hard = find("ACCT_PERIOD_HARD_CLOSED");
  if (hard !== undefined) {
    return { kind: "use_date", reason: "HARD_CLOSED", date: text(hard.args.firstOpenDate) };
  }
  const none = find("ACCT_PERIOD_NOT_FOUND");
  if (none !== undefined) {
    return { kind: "use_date", reason: "NOT_FOUND", date: text(none.args.firstOpenDate) };
  }
  const soft = find("ACCT_PERIOD_SOFT_CLOSED");
  const periodId = text(soft?.args.periodId);
  // Never a constant: reversing may need REVERSE, then ALLOCATE.
  const operation = text(soft?.args.operation);
  if (soft !== undefined && periodId !== null && operation !== null) {
    // A pre-check lists every operation the date needs; the ledger's own refusal names one.
    const listed = readOperations(soft.args.operations);
    const missing =
      listed === null ? [operation] : listed.filter((o) => !o.held).map((o) => o.operation);
    return {
      kind: "exception",
      periodId,
      operation,
      operations: missing,
      canOpen: soft.args.canOpenException === true,
      // Listed: covered once every operation is (the top-level flag means an exception for
      // ANOTHER operation). A database race carries no canOpenException: the retry tells more.
      tryAgain:
        listed !== null
          ? missing.length === 0
          : soft.args.viewerHasException === true || !("canOpenException" in soft.args),
    };
  }
  return null;
}

/**
 * Who sets the missing default accounts: the viewer in Default accounts (`link`), whoever
 * manages the books' settings (`ask`), or Link support (`support`) when one is locked or per
 * bank, none is named, or the default accounts aren't read (loading, failed, not there yet).
 */
export function roleFix(
  action: { roles: readonly string[]; qualified: boolean },
  mappings: readonly Pick<RoleMapping, "role" | "locked">[] | undefined,
  canSet: boolean,
): "link" | "ask" | "support" {
  const open =
    !action.qualified &&
    action.roles.length > 0 &&
    action.roles.every((role) => {
      const view = mappings?.find((m) => m.role === role);
      return view !== undefined && view.locked == null;
    });
  if (!open) return "support";
  return canSet ? "link" : "ask";
}

const PERIOD_CODES = new Set([
  "ACCT_PERIOD_SOFT_CLOSED",
  "ACCT_PERIOD_HARD_CLOSED",
  "ACCT_PERIOD_NOT_FOUND",
]);
/** Leads that may come with no violations (a pre-check, the database, a stock count's own): read as one problem. */
const LONE_CODES = new Set([...PERIOD_CODES, "ACCT_ROLE_NOT_MAPPED", "ACCT_BEFORE_BOOKS_START"]);
export const GENERIC = "accounting:ledger.journal.error.generic";

/** Refusals that carry no violations, as one sentence each. */
export const MESSAGE_KEYS: Readonly<Record<string, string>> = {
  PRODUCT_LOCKED: "accounting:ledger.journal.error.product_locked",
  PRODUCT_READ_ONLY: "accounting:ledger.journal.error.read_only",
  NOT_FOUND: "accounting:ledger.journal.error.not_found",
  VALIDATION_FAILED: "accounting:ledger.journal.error.invalid",
  FILE_TOO_LARGE: "accounting:ledger.journal.files.too_large",
  STORAGE_UNSUPPORTED_CONTENT_TYPE: "accounting:ledger.journal.files.unsupported",
  ACCT_INVARIANT_VIOLATION: "accounting:ledger.problems.ACCT_INVARIANT_VIOLATION",
  ACCT_POSTING_SOURCE_CONFLICT: "accounting:ledger.problems.ACCT_POSTING_SOURCE_CONFLICT",
  ACCT_PERIOD_STATE_CONFLICT: "accounting:ledger.problems.ACCT_PERIOD_STATE_CONFLICT",
  ACCT_LOCK_EXCEPTION_TOO_LONG: "accounting:ledger.problems.ACCT_LOCK_EXCEPTION_TOO_LONG",
  ACCT_ENTRY_ALREADY_REVERSED: "accounting:ledger.problems.ACCT_ENTRY_ALREADY_REVERSED",
  // A grant without the call's code; a company really lost also goes home (the signed-in layout).
  COMPANY_ACCESS_REVOKED: "accounting:app.not_allowed",
  // What the server will answer for a missing code once it tells the two apart.
  ACCESS_DENIED: "accounting:app.not_allowed",
};

/**
 * Supplier bill and credit refusals: no violations, their facts at the top of
 * args. Each is one sentence under `accounting:bills.error.{code}`, seeded EN and AR.
 */
export const BILL_ERROR_CODES = [
  "ACCT_ADVANCE_STALE",
  "ACCT_BILL_CHECKS_OPEN",
  "ACCT_DISMISSAL_REASON_REQUIRED",
  "ACCT_DOCUMENT_NOT_APPROVABLE",
  "ACCT_DOCUMENT_NOT_POSTED",
  "ACCT_DOCUMENT_NOT_VOIDABLE",
  "ACCT_PENDING_TAX_EXPIRED",
  "ACCT_SUPPLIER_PACK_MISSING",
  "IDEMPOTENCY_KEY_CONFLICT",
  "MISSING_IDEMPOTENCY_KEY",
  "STORAGE_KEY_NOT_FOUND",
] as const;

/**
 * Bill refusals worded by the value one arg names: `accounting:bills.error.{code}.{value}`,
 * every value seeded EN and AR.
 */
export const BILL_ERROR_REASONS = {
  ACCT_CREDIT_NOT_APPLICABLE: {
    arg: "reason",
    values: [
      "NOT_POSTED",
      "SUPPLIER_MISMATCH",
      "CURRENCY_MISMATCH",
      "FOREIGN_CURRENCY",
      "EXCEEDS_REMAINING",
      "PAIR_INVALID",
      "NOT_APPLIED",
      "PAIRED_BY_PAYMENT",
    ],
  },
  ACCT_ADVANCE_NOT_APPLICABLE: {
    arg: "reason",
    values: [
      "NOT_POSTED",
      "NOT_RELEASED",
      "SUPPLIER_MISMATCH",
      "CURRENCY_MISMATCH",
      "FOREIGN_CURRENCY",
      "EXCEEDS_REMAINING",
      "PAIR_INVALID",
      "IMPORTED_SERVICE_BILL",
      "NOT_APPLIED",
      "PAIRED_BY_PAYMENT",
    ],
  },
  ACCT_WRITE_OFF_NOT_ALLOWED: {
    arg: "reason",
    values: [
      "NOT_POSTED",
      "FOREIGN_CURRENCY",
      "EXCEEDS_REMAINING",
      "STALE",
      "NOTHING_REQUESTED",
      "NOT_REVERSIBLE",
      "ACCOUNT_INVALID",
      "DATE",
    ],
  },
  ACCT_PENDING_TAX_NOT_RESOLVABLE: {
    arg: "reason",
    values: ["NOTHING_PENDING", "EVIDENCE_MISSING", "ACCOUNT_INVALID"],
  },
  ACCT_BILL_DUPLICATE: {
    arg: "kind",
    values: ["EINVOICE_UUID", "ATTACHMENT_HASH", "SUPPLIER_NUMBER"],
  },
} as const satisfies Record<string, { arg: string; values: readonly string[] }>;

/** Supplier payment refusals: one sentence each under `accounting:payments.error.{code}`. */
export const PAYMENT_ERROR_CODES = [
  "ACCT_PAYMENT_CHECKS_OPEN",
  "ACCT_PAYMENT_OVER_ALLOCATED",
  "ACCT_CHEQUE_NUMBER_DUPLICATE",
  "PRECONDITION_REQUIRED",
] as const;

/**
 * Payment refusals worded by the value one arg names: `accounting:payments.error.{code}.{value}`.
 * The arg can come null (a database backstop): a missing or unknown value reads `{code}.fallback`.
 */
export const PAYMENT_ERROR_REASONS = {
  ACCT_PAYMENT_WRONG_STATE: { arg: "state", values: SUPPLIER_PAYMENT_STATES },
  ACCT_PAYMENT_DUAL_CONTROL: { arg: "role", values: DUAL_CONTROL_ROLES },
  ACCT_PAYMENT_ALLOCATION_INVALID: {
    arg: "reason",
    values: [
      "FOREIGN_SUPPLIER",
      "NOT_AN_ITEM_OF_DOCUMENT",
      "DUPLICATE_ITEM",
      "UNKNOWN_DOCUMENT",
      "KIND_MISMATCH",
    ],
  },
  ACCT_PAYMENT_NOT_REVERSIBLE: { arg: "reason", values: ["ADVANCE_USED", "DATE_BEFORE_PAYMENT"] },
  ACCT_IMPORTED_TAX_NOT_SETTLEABLE: {
    arg: "reason",
    values: [
      "NOTHING_DUE",
      "ALREADY_SETTLED",
      "RECEIPT_DATE",
      "PAY_FROM_INVALID",
      "RECEIPT_NUMBER_INVALID",
    ],
  },
} as const satisfies Record<string, { arg: string; values: readonly string[] }>;

/** Bank account and movement refusals with no reason: `accounting:banking.error.{code}`. */
export const BANK_ERROR_CODES = [
  "ACCT_BANK_ACCOUNT_DUPLICATE",
  "ACCT_BANK_ACCOUNT_INACTIVE",
] as const;

/**
 * Bank refusals worded by the value one arg names: `accounting:banking.error.{code}.{value}`;
 * a missing or unknown value reads `{code}.fallback`. A movement in the wrong state reads
 * `{code}.WRONG_STATE.{state}` (`.WRONG_STATE.by.{state}` when the server says who and when),
 * an other account refused `{code}.CONTRA_ACCOUNT_INVALID.{accountReason}`.
 */
export const BANK_ERROR_REASONS = {
  ACCT_BANK_ACCOUNT_INVALID: {
    arg: "reason",
    values: [
      "NOT_A_BANK_ACCOUNT",
      "NOT_RECONCILABLE",
      "INACTIVE",
      "IBAN_INVALID",
      "IBAN_COUNTRY",
      "BANK_UNKNOWN",
      "CURRENCY",
      "TYPE",
      "ACTIVE_TO_IN_FUTURE",
    ],
  },
  ACCT_BANK_ACCOUNT_NOT_RETIRABLE: {
    arg: "reason",
    values: [
      "UNHANDLED_LINES",
      "OPEN_SESSION",
      "UNCLEARED_ITEMS",
      "IN_TRANSIT",
      "BALANCE",
      "LATER_LINES",
    ],
  },
  ACCT_BANK_MOVEMENT_INVALID: {
    arg: "reason",
    values: [
      "KIND_CATEGORY",
      "ACCOUNT_NOT_MONEY",
      "TO_ACCOUNT_REQUIRED",
      "TO_ACCOUNT_SAME",
      "TO_AMOUNT_REQUIRED",
      "TO_AMOUNT_NOT_ALLOWED",
      "WHT_NOT_ALLOWED",
      "IN_DATE_BEFORE_OUT",
      "AMOUNT_SCALE",
    ],
  },
  ACCT_BANK_MOVEMENT_STATE: { arg: "reason", values: ["NOT_IN_TRANSIT", "MATCHED"] },
} as const satisfies Record<string, { arg: string; values: readonly string[] }>;

/** `ACCT_BANK_MOVEMENT_INVALID {reason: CONTRA_ACCOUNT_INVALID}`'s `accountReason`. */
export const CONTRA_ACCOUNT_REASONS = [
  "NOT_FOUND",
  "GROUP",
  "ACCOUNT_IS_BANK",
  "ACCOUNT_IS_SUSPENSE",
  "ACCOUNT_IS_SALES",
] as const;

/**
 * Bank sentences that name a fact the server may leave out: with every arg listed here,
 * `{key}_{suffix}` reads; without one, `{key}` alone (it names none).
 */
export const BANK_FACTS: Readonly<Record<string, readonly [string, ...string[]]>> = {
  ACCT_BANK_ACCOUNT_INACTIVE: ["DATE", "activeTo"],
  "ACCT_BANK_ACCOUNT_NOT_RETIRABLE.UNCLEARED_ITEMS": ["COUNT", "count"],
  "ACCT_BANK_ACCOUNT_NOT_RETIRABLE.IN_TRANSIT": ["COUNT", "count"],
  "ACCT_BANK_ACCOUNT_NOT_RETIRABLE.BALANCE": ["AMOUNT", "balance"],
  "ACCT_BANK_ACCOUNT_NOT_RETIRABLE.LATER_LINES": ["COUNT", "count"],
  ACCT_FX_RATE_MISSING: ["DAY", "currency", "date"],
};

/** Chart and default-account refusals with no reason: `accounting:ledger.error.{code}` (`LEDGER_FACTS` names their variants). */
export const LEDGER_ERROR_CODES = [
  "ACCT_ACCOUNT_CYCLE",
  "ACCT_ACCOUNT_PARENT_NOT_GROUP",
  "ACCT_ACCOUNT_SIDE_MISMATCH",
  "ACCT_CHART_TOO_DEEP",
  "ACCT_ROLE_MAPPING_STALE",
] as const;

/**
 * Ledger refusals worded by the value one arg names: `accounting:ledger.error.{code}.{value}`;
 * a missing or unknown value reads `{code}.fallback`. `ACCT_ACCOUNT_CHANGE_BLOCKED` reads
 * `{code}.{change}.{reason}` (`ACCOUNT_CHANGE_BLOCKED_REASONS`).
 */
export const LEDGER_ERROR_REASONS = {
  ACCT_ACCOUNT_USED: { arg: "field", values: ["code"] },
  ACCT_REQUIRED_DIMENSION_NOT_ALLOWED: { arg: "reason", values: REQUIRED_DIMENSION_REASONS },
  ACCT_ROLE_LOCKED: { arg: "reason", values: ROLE_LOCKED_REASONS },
  ACCT_ROLE_MAPPING_INVALID: { arg: "reason", values: ROLE_MAPPING_INVALID_REASONS },
  ACCT_ROLE_ACCOUNT_INVALID: { arg: "reason", values: ROLE_ACCOUNT_INVALID_REASONS },
  ACCT_INVENTORY_COUNT_INVALID: { arg: "reason", values: INVENTORY_COUNT_REASONS },
} as const satisfies Record<string, { arg: string; values: readonly string[] }>;

/**
 * Ledger sentences that name a fact the server may leave out (a database backstop): with
 * every arg listed here, `{key}_{suffix}` reads; without one, `{key}` alone (it names none).
 */
export const LEDGER_FACTS: Readonly<Record<string, readonly [string, ...string[]]>> = {
  ACCT_CHART_TOO_DEEP: ["DEPTH", "limit", "depth"],
  ACCT_ROLE_MAPPING_STALE: ["ACCOUNT", "current"],
  "ACCT_ACCOUNT_CHANGE_BLOCKED.RETIRE.HAS_LATER_LINES": ["DATE", "lastPostedOn"],
  "ACCT_ACCOUNT_CHANGE_BLOCKED.RETIRE.ROLE_MAPPED": ["NAMED", "roles"],
  "ACCT_REQUIRED_DIMENSION_NOT_ALLOWED.ENGINE_ACCOUNT": ["ACCOUNTS", "accounts"],
  "ACCT_ROLE_MAPPING_INVALID.ACCOUNT_HAS_ROLE": ["NAMED", "otherRole"],
  "ACCT_ROLE_MAPPING_INVALID.ACCOUNT_HAS_BALANCE": ["AMOUNT", "balance"],
  "ACCT_ROLE_MAPPING_INVALID.DATE": ["EARLIEST", "earliest"],
  "ACCT_INVENTORY_COUNT_INVALID.NO_DIFFERENCE": ["AMOUNT", "bookValue"],
  "ACCT_INVENTORY_COUNT_INVALID.BEFORE_LATEST_COUNT": ["DATE", "latestCountOn"],
};

/** A fact the server sent: a number, a non-empty string or list, or an account (`{code}`). */
const present = (value: unknown): boolean =>
  typeof value === "number" || codesIn(Array.isArray(value) ? value : [value]).length > 0;

/** An account the server names (`AcctAccountRef`) as code and name, or "". */
function accountRef(f: LedgerFormat, value: unknown): string {
  if (value === null || typeof value !== "object") return "";
  const ref = value as { code?: unknown; name?: Record<string, string> };
  const code = text(ref.code);
  if (code === null) return "";
  const name = f.name(ref.name);
  return name !== "" ? `${code} ${name}` : code;
}

const isOneOf = (list: readonly string[], value: string | null): value is string =>
  value !== null && list.includes(value);

/** The `accounting:ledger.error.*` sentence for a chart, default-account or stock-count refusal; null for another code. */
function ledgerKey(code: string, args: Record<string, unknown>): string | null {
  let key: string;
  if (code === "ACCT_ACCOUNT_CHANGE_BLOCKED") {
    const change = text(args.change);
    const reasons: readonly string[] =
      change !== null && change in ACCOUNT_CHANGE_BLOCKED_REASONS
        ? ACCOUNT_CHANGE_BLOCKED_REASONS[change as keyof typeof ACCOUNT_CHANGE_BLOCKED_REASONS]
        : [];
    const reason = text(args.reason);
    key = isOneOf(reasons, reason) ? `${code}.${String(change)}.${reason}` : `${code}.fallback`;
  } else if (code === "ACCT_ROLE_MAPPING_STALE" && args.current === null) {
    // No account holds it now; `current` left out says nothing, so the plain sentence reads.
    key = `${code}_UNMAPPED`;
  } else if (code in LEDGER_ERROR_REASONS) {
    const { arg, values } = LEDGER_ERROR_REASONS[code as keyof typeof LEDGER_ERROR_REASONS];
    const value = text(args[arg]);
    key = isOneOf(values, value) ? `${code}.${value}` : `${code}.fallback`;
  } else if (isOneOf(LEDGER_ERROR_CODES, code)) key = code;
  else return null;
  const fact = LEDGER_FACTS[key];
  const named = fact !== undefined && fact.slice(1).every((name) => present(args[name]));
  return `accounting:ledger.error.${named ? `${key}_${fact[0]}` : key}`;
}

/**
 * The words a chart, default-account, stock-count or bank sentence names, "" when the server
 * left the fact out (`ledgerKey` and `bankKey` then pick a sentence without it). `currency`: the
 * books' own (a bank account's own for its balance), for a balance that names none.
 */
export function ledgerErrorParams(
  f: LedgerFormat,
  t: Translate,
  i18n: Exists,
  args: Record<string, unknown>,
  currency: string,
): Record<string, string | number> {
  const day =
    text(args.lastPostedOn) ?? text(args.earliest) ?? text(args.latestCountOn) ?? text(args.date);
  const activeTo = text(args.activeTo);
  const at = text(args.at);
  // With its currency, and kept in its own direction inside an Arabic sentence.
  const amount = (value: unknown, of: string): string => {
    const figure = text(value);
    return figure !== null && of !== "" ? isolate(`${f.money(figure, of)} ${of}`) : "";
  };
  const count = (value: unknown): string =>
    typeof value === "number" ? f.number(value) : (text(value) ?? "");
  const roles = (value: unknown): string =>
    labelList(f, t, i18n, "accounting:ledger.account_roles", codesIn(value), "conjunction");
  return {
    date: day !== null ? f.day(day) : "",
    roles: roles(args.roles),
    role: roles([args.role]),
    otherRole: roles([args.otherRole]),
    accounts: new Intl.ListFormat(intlLocale(f.locale), { type: "conjunction" }).format(
      codesIn(args.accounts),
    ),
    account: accountRef(f, args.current),
    // Debit-positive from the server (a liability's balance comes negative): the sentence names how much, not the side.
    balance: amount(text(args.balance)?.replace(/^-/, "") ?? null, currency),
    bookValue: amount(args.bookValue, text(args.currency) ?? currency),
    limit: count(args.limit),
    depth: count(args.depth),
    // A plural's count stays a number: ICU picks the form and writes the numerals.
    count: typeof args.count === "number" ? args.count : "",
    currency: text(args.currency) ?? "",
    activeTo: activeTo !== null ? f.day(activeTo) : "",
    at: at !== null ? f.instant(at) : "",
  };
}

/** A person's or an actor's name, when the server knows it. */
const nameOf = (value: unknown): string | null =>
  value !== null && typeof value === "object" ? text((value as { name?: unknown }).name) : null;

/** The words a refusal's sentence may name; `messageKey` picks a sentence only when its word is there. */
export const messageParams = (args: Record<string, unknown>): Record<string, string> => ({
  by: nameOf(args.by) ?? "",
  newLast4: text(args.newLast4) ?? "",
});

/**
 * The `accounting:banking.error.*` sentence for a bank account or movement refusal, or for a
 * rate missing on a field (a movement's: it goes on that field, not to the rate table); null
 * for another code.
 */
function bankKey(code: string, args: Record<string, unknown>): string | null {
  const reason = text(args.reason);
  let key: string;
  if (code === "ACCT_FX_RATE_MISSING") {
    if (text(args.field) === null) return null;
    key = code;
  } else if (code === "ACCT_BANK_MOVEMENT_STATE" && reason === "WRONG_STATE") {
    const state = text(args.state);
    // Who moved it into its state and when; a DRAFT has no stamp.
    const by = nameOf(args.by) !== null && text(args.at) !== null ? ".by" : "";
    key = isOneOf(MOVEMENT_STATES, state)
      ? `${code}.WRONG_STATE${by}.${state}`
      : `${code}.fallback`;
  } else if (code === "ACCT_BANK_MOVEMENT_INVALID" && reason === "CONTRA_ACCOUNT_INVALID") {
    const why = text(args.accountReason);
    key = `${code}.${reason}.${isOneOf(CONTRA_ACCOUNT_REASONS, why) ? why : "fallback"}`;
  } else if (code in BANK_ERROR_REASONS) {
    const { arg, values } = BANK_ERROR_REASONS[code as keyof typeof BANK_ERROR_REASONS];
    const value = text(args[arg]);
    key = isOneOf(values, value) ? `${code}.${value}` : `${code}.fallback`;
  } else if (isOneOf(BANK_ERROR_CODES, code)) key = code;
  else return null;
  const fact = BANK_FACTS[key];
  const named = fact !== undefined && fact.slice(1).every((name) => present(args[name]));
  return `accounting:banking.error.${named ? `${key}_${fact[0]}` : key}`;
}

/**
 * The sentence for a refusal that carries no violations, from its code and the
 * reason or kind its args name. Pure, so a batch item's `{errorCode, args}` reads
 * the same way as a single refusal. An unknown code or value is the generic sentence.
 */
export function messageKey(code: string, args: Record<string, unknown>): string {
  if (code === "ACCT_ENTRY_NOT_REVERSIBLE") {
    // A credit's own refusal names its reason at the top (the ledger's sits in violations).
    const reason = text(args.reason);
    return isOneOf(NOT_REVERSIBLE_REASONS, reason)
      ? `accounting:ledger.entry.not_reversible.${reason}`
      : "accounting:ledger.problems.ACCT_ENTRY_NOT_REVERSIBLE";
  }
  // This posted bill already carries another e-invoice: its own uuid, no `original`.
  if (code === "ACCT_BILL_DUPLICATE" && text(args.einvoiceUuid) !== null)
    return "accounting:bills.error.ACCT_BILL_DUPLICATE.EINVOICE_ALREADY_SET";
  if (code in BILL_ERROR_REASONS) {
    const { arg, values } = BILL_ERROR_REASONS[code as keyof typeof BILL_ERROR_REASONS];
    const value = text(args[arg]);
    return isOneOf(values, value) ? `accounting:bills.error.${code}.${value}` : GENERIC;
  }
  if (isOneOf(BILL_ERROR_CODES, code)) return `accounting:bills.error.${code}`;
  // The supplier's new account, or none verified now (the server's backstop names no digits).
  if (code === "ACCT_PAYEE_NOT_VERIFIED")
    return text(args.newLast4) !== null
      ? "accounting:payments.error.ACCT_PAYEE_NOT_VERIFIED.new_account"
      : "accounting:payments.error.ACCT_PAYEE_NOT_VERIFIED.no_account";
  // Releasing what someone who worked on it approved before two people were needed: it needs approving again.
  if (
    code === "ACCT_PAYMENT_DUAL_CONTROL" &&
    text(args.action) === "RELEASE" &&
    text(args.role) === "APPROVER"
  )
    return "accounting:payments.error.ACCT_PAYMENT_DUAL_CONTROL.approved_by_editor";
  if (code in PAYMENT_ERROR_REASONS) {
    const root = `accounting:payments.error.${code}`;
    const { arg, values } = PAYMENT_ERROR_REASONS[code as keyof typeof PAYMENT_ERROR_REASONS];
    const value = text(args[arg]);
    if (!isOneOf(values, value)) return `${root}.fallback`;
    // Who acted first, when the server knows.
    return code === "ACCT_PAYMENT_WRONG_STATE" && nameOf(args.by) !== null
      ? `${root}.by.${value}`
      : `${root}.${value}`;
  }
  if (isOneOf(PAYMENT_ERROR_CODES, code)) return `accounting:payments.error.${code}`;
  return bankKey(code, args) ?? ledgerKey(code, args) ?? MESSAGE_KEYS[code] ?? GENERIC;
}

/** A check a refusal names (`ACCT_BILL_CHECKS_OPEN {checks}`, a blocked batch item). */
export interface RefusedCheck {
  id: string;
  code: string;
  fieldPath: string | null;
}

/** The checks in a refusal's args, whatever arrives. */
export function readChecks(args: Record<string, unknown>): RefusedCheck[] {
  if (!Array.isArray(args.checks)) return [];
  return args.checks.flatMap((item: unknown) => {
    if (item === null || typeof item !== "object") return [];
    const { id, code, fieldPath } = item as Record<string, unknown>;
    if (typeof id !== "string" || typeof code !== "string") return [];
    return [{ id, code, fieldPath: text(fieldPath) }];
  });
}

export interface Refusal {
  action: Action;
  /** The violations, for their lines (or the entry when they have none). */
  problems: Problem[];
  /** The refusal's own facts (the top of args, without violations): `checks`, `original`, `state`… */
  args: Record<string, unknown>;
}

/** What a refused save, post, approve, delete, reverse or exception asks of the person. */
export function readRefusal(error: unknown): Refusal {
  const stale = readStale(error);
  const { violations, ...top } = problemArgs(error);
  if (stale !== null)
    return {
      action: { kind: "stale", name: stale.by, reason: stale.reason },
      problems: [],
      args: top,
    };
  if (!isApiError(error))
    return { action: { kind: "message", key: GENERIC }, problems: [], args: {} };
  // A period refusal's figures also sit at the top of args, whichever violation leads.
  const listed = readProblems(violations).map((p) =>
    PERIOD_CODES.has(p.code) ? { ...p, args: { ...top, ...p.args } } : p,
  );
  // A pre-check's (or the database's) period refusal carries no violations, only those figures;
  // so may a stock count's missing default account or date before the books start.
  const problems =
    listed.length === 0 && LONE_CODES.has(error.errorCode)
      ? [{ lineNo: null, code: error.errorCode, args: top }]
      : listed;
  if (problems.length > 0) {
    return { action: actionFor(problems) ?? { kind: "problems" }, problems, args: top };
  }
  return {
    action: { kind: "message", key: messageKey(error.errorCode, top) },
    problems: [],
    args: top,
  };
}

// ---- Dates the refusals turn on ----------------------------------------------------

/** A reversal goes on today in the company, or on the entry's own date when that is later. */
export const reverseDateDefault = (entryDate: string, today: string): string =>
  today < entryDate ? entryDate : today;

/** Before its entry's own date a reversal is refused (DATE_BEFORE_ORIGINAL). */
export const reverseDateTooEarly = (date: string, entryDate: string): boolean => date < entryDate;

/**
 * The day a lock exception opened today ends, when no expiry is sent: the
 * day before the day seven days from now falls on, in the company's time.
 */
export function exceptionEndsOn(today: string): string {
  const day = new Date(`${today}T00:00:00Z`);
  day.setUTCDate(day.getUTCDate() + 6);
  return day.toISOString().slice(0, 10);
}
